import {
  BaseSyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import Button from 'react-bootstrap/Button'
import 'lib/reactBootstrapReact19/button'
import FilterDropdownWrapper from 'common/FilterDropdownWrapper'
import filterPillStyles from 'common/FilterDropdownWrapper/index.module.scss'
import Icon from 'common/Icon'
import { SLATE_GRAY, WHITE } from 'constants/colors'
import multiselectStyles from 'common/MultiselectDropdown/index.module.scss'
import OptionsPanel from './OptionsPanel'

export type ControlledPillMultiselectFilterVariant = 'default' | 'panel-only'

const noopOpenChange = (_open: boolean): void => {
  return
}

type ControlledPillMultiselectFilterBaseProps = {
  options: ProviderFilter[]
  selectedIds: string[]
  onSelectionChange: (nextSelectedIds: string[]) => void
  className?: string
  disabled?: boolean
  listItemIdPrefix?: string
  containerClassName?: string
  /** Default `below`. Use `above` if the menu must open upward. */
  dropdownPlacement?: 'below' | 'above'
  /**
   * `end`: anchor the panel to the pill’s right edge (opens leftward).
   * `auto`: keep a start anchor and shift horizontally so the panel stays inside the viewport
   * (calendar modal: rightmost pill vs wrapped left pill).
   */
  dropdownAlign?: 'start' | 'end' | 'auto'
  /** Overrides default `zindex-dropdown` (e.g. `zindex-popover` in modals). */
  dropdownSurfaceClassName?: string
  /** `compact` uses a narrower max width and tighter padding than the default 350px panel. */
  dropdownMenuSize?: 'default' | 'compact'
  /** When true, uses Navy (#14467C) styling for checkboxes (calendar filter modal). */
  useNavyCheckbox?: boolean
  /** When true, renders dropdown with fixed positioning via portal (for horizontal scroll containers). */
  useFixedDropdown?: boolean
}

export type ControlledPillMultiselectFilterProps =
  | (ControlledPillMultiselectFilterBaseProps & {
      variant?: 'default'
      placeholder: string
      /** Summary shown on the pill when there are selections (usually computed by the parent). */
      pillSummaryText: string
      isOpen: boolean
      onOpenChange: (open: boolean) => void
      /**
       * When true, the pill toggles open state but the checklist is not attached under the pill.
       * Render `variant="panel-only"` elsewhere (e.g. in a modal) with the same selection props.
       */
      suppressAttachedDropdown?: boolean
    })
  | (ControlledPillMultiselectFilterBaseProps & {
      variant: 'panel-only'
    })

/**
 * Controlled pill + multiselect checklist for `ProviderFilter` options (profile filters, etc.).
 * Open state, selection, and summaries are owned by the parent.
 */
const ControlledPillMultiselectFilter: React.FC<ControlledPillMultiselectFilterProps> = (
  props
) => {
  const {
    options,
    selectedIds,
    onSelectionChange,
    className,
    disabled = false,
    listItemIdPrefix = 'pill-multiselect',
    containerClassName,
    dropdownPlacement = 'below',
    dropdownAlign = 'start',
    dropdownSurfaceClassName,
    dropdownMenuSize = 'default',
    useNavyCheckbox = false,
    useFixedDropdown = false,
  } = props

  const onToggle = (option: ProviderFilter, currentlySelected: boolean) => {
    if (disabled) {
      return
    }

    if (currentlySelected) {
      onSelectionChange(selectedIds.filter((id) => id !== option.id))
    } else {
      onSelectionChange([...selectedIds, option.id])
    }
  }

  const clearSelected = () => {
    if (disabled) {
      return
    }

    onSelectionChange([])
  }

  const isPanelOnly = 'variant' in props && props.variant === 'panel-only'

  const placeholder = isPanelOnly ? '' : props.placeholder
  const pillSummaryText = isPanelOnly ? '' : props.pillSummaryText
  const isOpen = isPanelOnly ? false : props.isOpen
  const onOpenChange = isPanelOnly ? noopOpenChange : props.onOpenChange
  const suppressAttachedDropdown = isPanelOnly
    ? true
    : props.suppressAttachedDropdown ?? false

  const showAttachedList = isOpen && !suppressAttachedDropdown && !disabled
  const hasValue = !!pillSummaryText
  const isAutoDropdownAlign = dropdownAlign === 'auto'
  const anchorRef = useRef<HTMLDivElement>(null)
  const pillContainerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [fixedPosition, setFixedPosition] = useState<{
    top: number
    left: number
  } | null>(null)

  // Handle outside click for portal-rendered dropdown
  useEffect(() => {
    if (!useFixedDropdown || !showAttachedList) {
      return
    }

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node

      // Check if click is outside both the pill and the dropdown
      const isOutsidePill =
        pillContainerRef.current && !pillContainerRef.current.contains(target)
      const isOutsideDropdown =
        dropdownRef.current && !dropdownRef.current.contains(target)

      if (isOutsidePill && isOutsideDropdown) {
        onOpenChange(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [useFixedDropdown, showAttachedList, onOpenChange])

  useLayoutEffect(() => {
    if (!useFixedDropdown || !showAttachedList) {
      setFixedPosition(null)
      return
    }

    const updatePosition = () => {
      const rect = pillContainerRef.current?.getBoundingClientRect()

      if (!rect) {
        return
      }

      const margin = 16
      const vw = window.innerWidth
      const nextTop = rect.bottom + 8
      // Use max-width for compact (272px) and width for default (350px) per SCSS
      const dropdownWidth =
        dropdownMenuSize === 'compact' ? Math.min(272, vw - margin * 2) : 350

      // Close when the pill leaves the viewport — clamping alone would pin the
      // portal to an edge while the horizontally scrolling drawer row moves away.
      if (
        nextTop < margin ||
        rect.bottom < 8 ||
        rect.top > window.innerHeight - 8 ||
        rect.right < margin ||
        rect.left > vw - margin
      ) {
        onOpenChange(false)
        return
      }

      let preferredLeft = rect.left

      if (dropdownAlign === 'end') {
        preferredLeft = rect.right - dropdownWidth
      }

      const minLeft = margin
      const maxLeft = vw - margin - dropdownWidth
      const left =
        maxLeft < minLeft
          ? minLeft
          : Math.max(minLeft, Math.min(preferredLeft, maxLeft))

      setFixedPosition({
        top: nextTop,
        left,
      })
    }

    updatePosition()
    // Ref can lag one frame; retry so we never portal fixed content without coords
    const rafId = window.requestAnimationFrame(updatePosition)

    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [
    useFixedDropdown,
    showAttachedList,
    dropdownMenuSize,
    dropdownAlign,
    pillSummaryText,
    onOpenChange,
  ])
  const [autoPanelShiftX, setAutoPanelShiftX] = useState(0)

  const updateAutoPanelShift = useCallback(() => {
    if (
      !isAutoDropdownAlign ||
      !showAttachedList ||
      typeof window === 'undefined'
    ) {
      return
    }

    const el = anchorRef.current

    if (!el) {
      return
    }

    const pill = el.getBoundingClientRect()
    const vw = window.innerWidth
    const margin = 12
    const panelW = Math.min(272, vw - 48)
    const minLeft = margin
    const maxLeft = vw - margin - panelW
    const idealLeft =
      maxLeft < minLeft
        ? minLeft
        : Math.max(minLeft, Math.min(pill.left, maxLeft))

    setAutoPanelShiftX(idealLeft - pill.left)
  }, [isAutoDropdownAlign, showAttachedList])

  useLayoutEffect(() => {
    if (!isAutoDropdownAlign || !showAttachedList) {
      setAutoPanelShiftX(0)

      return
    }

    if (typeof window === 'undefined') {
      return
    }

    updateAutoPanelShift()
    window.addEventListener('resize', updateAutoPanelShift)

    return () => {
      window.removeEventListener('resize', updateAutoPanelShift)
    }
  }, [
    isAutoDropdownAlign,
    showAttachedList,
    updateAutoPanelShift,
    pillSummaryText,
  ])

  if (isPanelOnly) {
    const panelContainerClass =
      dropdownMenuSize === 'compact'
        ? multiselectStyles.filter_new_dropdown_container_compact
        : multiselectStyles.filter_new_dropdown_container

    return (
      <div
        className={classNames(
          'bg-white zindex-dropdown filter-new-dropdown-container',
          panelContainerClass,
          className
        )}
      >
        <OptionsPanel
          options={options}
          selectedIds={selectedIds}
          onToggle={onToggle}
          disabled={disabled}
          listItemIdPrefix={listItemIdPrefix}
          paddingSize={dropdownMenuSize === 'compact' ? 'compact' : 'default'}
          containerClassName={containerClassName}
          useNavyCheckbox={useNavyCheckbox}
        />
      </div>
    )
  }

  if (suppressAttachedDropdown) {
    return (
      <div
        className={classNames(
          '~inline-flex ~min-w-0 ~max-w-full ~flex-col',
          className
        )}
      >
        <div className={filterPillStyles.wrapper}>
          <Button
            variant="none"
            disabled={disabled}
            aria-label={`${placeholder} dropdown`}
            onClick={() => onOpenChange(!isOpen)}
            className={classNames(
              'd-flex align-items-center',
              filterPillStyles.new_pill,
              {
                [filterPillStyles.pill_selected]: isOpen || hasValue,
              }
            )}
          >
            <span
              className={classNames(
                'mr-2 ~font-gibson ~text-sm ~font-normal ~leading-snug',
                filterPillStyles.pill_label
              )}
              data-text={pillSummaryText || placeholder}
            >
              {pillSummaryText || placeholder}
            </span>
            {hasValue ? (
              <div
                className={classNames(
                  filterPillStyles.close_icon,
                  'flex-center p-0 m-0'
                )}
                onClick={(event: BaseSyntheticEvent) => {
                  if (disabled) {
                    return
                  }

                  event.stopPropagation()
                  clearSelected()
                  onOpenChange(false)
                }}
              >
                <Icon
                  icon="filterCross"
                  fill={WHITE}
                  className={filterPillStyles.close_icon}
                />
              </div>
            ) : (
              <Icon
                icon={isOpen ? 'filterArrowUp' : 'filterArrowDown'}
                fill={isOpen ? WHITE : SLATE_GRAY}
                className={filterPillStyles.filter_icon}
              />
            )}
          </Button>
        </div>
      </div>
    )
  }

  const dropdownPanel = showAttachedList ? (
    <div
      ref={useFixedDropdown ? dropdownRef : undefined}
      className={classNames(
        'bg-white filter-new-dropdown-container ~rounded-lg ~shadow-lg',
        useFixedDropdown ? 'position-fixed' : 'position-absolute',
        dropdownSurfaceClassName ?? 'zindex-dropdown',
        dropdownMenuSize === 'compact'
          ? multiselectStyles.filter_new_dropdown_container_compact
          : multiselectStyles.filter_new_dropdown_container,
        {
          [multiselectStyles.dropdown_container]:
            !useFixedDropdown && dropdownPlacement === 'below',
          [multiselectStyles.dropdown_container_above]:
            !useFixedDropdown && dropdownPlacement === 'above',
          [multiselectStyles.dropdown_menu_align_end]:
            !useFixedDropdown && dropdownAlign === 'end',
        }
      )}
      style={
        useFixedDropdown && fixedPosition
          ? {
              position: 'fixed' as const,
              top: fixedPosition.top,
              left: fixedPosition.left,
              // Override global `.filter-new-dropdown-container { margin-top }` so
              // fixed coords are the sole source of vertical placement.
              marginTop: 0,
              zIndex: 1300,
              pointerEvents: 'auto' as const,
            }
          : isAutoDropdownAlign
          ? { transform: `translateX(${autoPanelShiftX}px)` }
          : undefined
      }
      onClick={(e: BaseSyntheticEvent) => e.stopPropagation()}
      onMouseDown={(e: BaseSyntheticEvent) => e.stopPropagation()}
      onTouchStart={(e: React.TouchEvent) => e.stopPropagation()}
    >
      <OptionsPanel
        options={options}
        selectedIds={selectedIds}
        onToggle={onToggle}
        disabled={disabled}
        listItemIdPrefix={listItemIdPrefix}
        paddingSize={dropdownMenuSize === 'compact' ? 'compact' : 'default'}
        containerClassName={containerClassName}
        useNavyCheckbox={useNavyCheckbox}
      />
    </div>
  ) : null

  return (
    <div
      ref={(el) => {
        if (isAutoDropdownAlign) {
          ;(anchorRef as React.MutableRefObject<HTMLDivElement | null>).current = el
        }

        ;(pillContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = el
      }}
      className={classNames(
        '~inline-flex ~min-w-0 ~max-w-full ~flex-col',
        className
      )}
    >
      <FilterDropdownWrapper
        placeholder={placeholder}
        value={pillSummaryText}
        onPillClick={(shouldShow) =>
          onOpenChange(useFixedDropdown ? !isOpen : shouldShow)
        }
        disabled={disabled}
        showDropdown={showAttachedList && !useFixedDropdown}
        onClearValue={() => {
          clearSelected()
          onOpenChange(false)
        }}
      >
        {!useFixedDropdown && dropdownPanel}
      </FilterDropdownWrapper>
      {/* Wait for measured coords — fixed without top/left paints at the top of the viewport */}
      {useFixedDropdown &&
        dropdownPanel &&
        fixedPosition &&
        typeof document !== 'undefined' &&
        createPortal(dropdownPanel, document.body)}
    </div>
  )
}

export default ControlledPillMultiselectFilter
